import axios from 'axios';
import router from '@/router';
import { useUserStore } from '@/store/user';
import { HTTP_UNAUTHORIZED, HTTP_FORBIDDEN } from '@/data/common/responseStatus';

const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_CMS,
    headers: {
        'Content-Type': 'application/json'
    }
});

/**
 * AXIOS INSTANCE that does not log the user out on HTTP_UNAUTHORIZED / HTTP_FORBIDDEN.
 *
 * The storefront catalog endpoint authenticates the forwarded CNM token rather than the CMS
 * JWT, and answers 401 when the backend no longer recognises the session (its identity cache
 * was cleared, say) or 403 when the caller is not entitled to a price group. Neither is a
 * reason to tear down an agent's whole portal session mid-browse - the view shows the backend's
 * message instead, which already tells them to log in again when that is the fix.
 */
export const axiosWithoutRedirect = axios.create({
    baseURL: import.meta.env.VITE_API_CMS,
    headers: {
        'Content-Type': 'application/json'
    }
});

// The request interceptor ensures that we check for the token
// in local storage every time an ajax request is made
function requestInterceptor(config) {
    const token = localStorage.getItem('cnm-cms-jwt-token') || null;
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    return localeInterceptor(config);
}

function localeInterceptor(config) {
    if (['get', 'post', 'patch', 'put', 'delete'].includes(config.method?.toLowerCase())) {
        config.params = config.params || {};
        config.params['locale'] = localStorage.getItem('cnm-locale') || 'es';
    }

    return config;
}

axiosInstance.interceptors.request.use(requestInterceptor, error => Promise.reject(error));

// No CMS JWT on this instance: its endpoints authenticate the CNM token, and a CMS JWT riding
// along could only hurt - once it expires, the firewall rejects the whole request with 401 even
// though the endpoint itself is public. That is what a user holding both a CMS and a portal role
// hit on the Catálogos page twelve hours after logging in.
axiosWithoutRedirect.interceptors.request.use(localeInterceptor, error => Promise.reject(error));

// response interceptor (For common tasks when detect error status code on response)
axiosInstance.interceptors.response.use(
    response => {
        return response;
    },
    err => {
        if (err.response?.status === HTTP_UNAUTHORIZED || err.response?.status === HTTP_FORBIDDEN) {
            if (router.currentRoute.value.name !== 'login') {
                const userStore = useUserStore();

                userStore.logout();
                router.replace({ name: 'login' });
            }
        }

        return Promise.reject(err);
    }
);

export default axiosInstance;
